import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError, getPortal } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Empty, Field, Money, Note, PageHeader, Panel, Rail, Spinner, Table, Tiles } from '../ui';
import {
  cohortValues, excludedValues, needsTwoNumberNote, notShownValues, pieceRows, productRows,
  type NonMovingProduct, type NonMovingReport, type NonMovingRetailer,
} from './analytics-non-moving';

/**
 * A-42 Analytics — AN-01…AN-12, one tab per SUBJECT and one request per tab (analytics.service.ts).
 *
 * Three things the screen must show rather than hide:
 *  - A refused total (AN-08, D72). When figures span currencies the combined figure is replaced by
 *    the reason, and each currency is listed on its own. There is never a number that looks like a
 *    total and is not one.
 *  - A report that is not yet possible (AN-10's request fields)
 *    says why, in the words the API gives, instead of rendering zeros.
 *  - An EMPTY report is correct when there is no data yet. Most of these read pieces, which the
 *    consignment modules have not started writing; an empty table there is the true answer.
 *
 * Filtering (AN-07): the date range is the form; retailer, group and category are applied by
 * clicking a row in a breakdown, which is how someone reading a report actually narrows it.
 */

/*
 * Mirrors `analytics.service.ts`'s SUBJECTS exactly, and in the same order — the tab order
 * follows this list, and a value missing from it makes the DB-12 drill-through fall back to
 * `inventory` without saying so. `non-moving` is AN-13 and is LAST so no existing tab moves.
 */
type Subject = 'inventory' | 'sales' | 'lead-time' | 'returns' | 'budget' | 'payables' | 'subscriptions' | 'non-moving';
const SUBJECTS: Subject[] = ['inventory', 'sales', 'lead-time', 'returns', 'budget', 'payables', 'subscriptions', 'non-moving'];

interface CurrencyAmount { currency: string; amount: string }
type Consolidated =
  | { kind: 'TOTAL'; currency: string; amount: string }
  | { kind: 'NONE' }
  | { kind: 'REFUSED'; decision: 'D72'; reason: string; currencies: string[] };
interface Unavailable { available: false; reason: string }
/** AN-11 — `budgetTrend()`'s row, as the API returns it. */
interface BudgetTrendRow {
  retailerId: string; retailerName: string; currency: string; lowUtilisation: boolean; lowForDays: number;
  series: Array<{ day: string; assigned: string; inUse: string; headroom: string; utilisationPct: number | null }>;
}
interface Row { key: string; label: string; [k: string]: unknown }
type Report = Record<string, unknown> & { period: { from: string; to: string }; today: string; notes: { style: string } };

interface Filters { from: string; to: string; retailerId?: string; groupId?: string; categoryId?: string }
type Drill = { field: 'retailerId' | 'groupId' | 'categoryId'; key: string; label: string };

const query = (f: Filters, extra: Record<string, string> = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...f, ...extra })) if (v) q.set(k, v);
  return q.toString();
};

function Amounts({ list }: { list: CurrencyAmount[] | undefined }) {
  if (!list || list.length === 0) return <span className="mk-dim">—</span>;
  return <>{list.map((m) => <div key={m.currency}><Money amount={m.amount} currency={m.currency} /></div>)}</>;
}

/** AN-08 — the combined figure, or the reason there is none. */
function Combined({ c }: { c: Consolidated }) {
  const { t } = useTranslation();
  if (c.kind === 'TOTAL') return <Money amount={c.amount} currency={c.currency} />;
  if (c.kind === 'NONE') return <span className="mk-dim">—</span>;
  return <Alert tone="them"><b>{t('analytics.noTotal')}</b> {c.reason}</Alert>;
}

function NotYet({ u }: { u: Unavailable }) {
  const { t } = useTranslation();
  return <Alert tone="us"><b>{t('analytics.notYet')}</b> {u.reason}</Alert>;
}

export function Analytics() {
  const { t } = useTranslation();
  const { me } = useSession();
  /*
   * DB-12 — a dashboard tile drills to /analytics?subject=payables, and this screen ignored the
   * parameter and always opened on Inventory, so the drill-through landed on the wrong tab. The
   * value is matched against SUBJECTS rather than cast, so a stale or hand-typed parameter falls
   * back instead of selecting a subject that does not exist.
   */
  const [params] = useSearchParams();
  const [subject, setSubject] = useState<Subject>(
    () => SUBJECTS.find((s) => s === params.get('subject')) ?? 'inventory',
  );
  const [filters, setFilters] = useState<Filters>({ from: '', to: '' });
  const [drill, setDrill] = useState<Drill[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);

  const portals = me?.kind === 'internal' ? me.portals : [];
  const caps = portals.find((p) => p.code === getPortal())?.capabilities;
  const subjects = SUBJECTS.filter((s) => s !== 'subscriptions' || caps?.subscriptions);
  const applied: Filters = { ...filters, ...Object.fromEntries(drill.map((d) => [d.field, d.key])) };

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', subject, applied],
    queryFn: () => api.get<Report>(`/analytics/${subject}?${query(applied)}`),
    enabled: !comparing,
  });

  const narrow = (field: Drill['field']) => (r: Row) => {
    if (r.key === '∅') return;
    setDrill((d) => [...d.filter((x) => x.field !== field), { field, key: r.key, label: r.label }]);
  };

  async function exportCsv() {
    setFailure(null);
    try {
      const portal = getPortal();
      const res = await fetch(`/api/analytics/${subject}?${query(applied, { format: 'csv' })}`, {
        credentials: 'same-origin', headers: portal ? { 'x-portal': portal } : {},
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `${subject}-${portal ?? 'portal'}-${data?.period.from ?? ''}-${data?.period.to ?? ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : t('analytics.exportFailed'));
    }
  }

  return (
    <>
      <PageHeader
        title={t('analytics.title')}
        sub={data ? t('analytics.sub', { from: data.period.from, to: data.period.to, today: data.today }) : undefined}
        actions={
          <>
            {portals.length > 1 ? (
              <Button tone="ghost" onClick={() => setComparing(!comparing)}>
                {comparing ? t('analytics.backToPortal') : t('analytics.compare')}
              </Button>
            ) : null}
            {!comparing ? <Button tone="ghost" onClick={() => void exportCsv()}>{t('analytics.export')}</Button> : null}
          </>
        }
      />

      <Panel pad>
        <div style={{ display: 'flex', gap: 'var(--mk-space-3)', flexWrap: 'wrap', alignItems: 'end' }}>
          <Field label={t('analytics.from')}>
            <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          </Field>
          <Field label={t('analytics.to')}>
            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          </Field>
          {drill.map((d) => (
            <Button key={d.field} size="sm" tone="ghost" onClick={() => setDrill(drill.filter((x) => x.field !== d.field))}>
              {t(`analytics.filter.${d.field}`)}: {d.label} ✕
            </Button>
          ))}
          {filters.from || filters.to || drill.length
            ? <Button size="sm" tone="ghost" onClick={() => { setFilters({ from: '', to: '' }); setDrill([]); }}>{t('analytics.clear')}</Button>
            : null}
        </div>
      </Panel>

      {failure ? <Alert tone="stop">{failure}</Alert> : null}

      {comparing ? <Compare filters={filters} /> : (
        <>
          <div style={{ display: 'flex', gap: 'var(--mk-space-2)', flexWrap: 'wrap', margin: 'var(--mk-space-4) 0' }}>
            {subjects.map((s) => (
              <Button key={s} size="sm" tone={s === subject ? 'solid' : 'ghost'} onClick={() => setSubject(s)}>
                {t(`analytics.subject.${s}`)}
              </Button>
            ))}
          </div>
          {isLoading ? <Spinner /> : null}
          {error ? <Alert tone="stop">{error.message}</Alert> : null}
          {data ? <SubjectView subject={subject} r={data} narrow={narrow} /> : null}
          {data ? <Note>{data.notes.style}</Note> : null}
        </>
      )}
    </>
  );
}

function Breakdown(
  { title, rows, columns, onPick }:
  { title: string; rows: Row[]; columns: Array<{ header: string; render: (r: Row) => ReactNode; numeric?: boolean }>; onPick?: (r: Row) => void },
) {
  const { t } = useTranslation();
  return (
    <Panel title={title}>
      <Table
        rows={rows}
        rowKey={(r) => r.key}
        empty={<Empty title={t('analytics.emptyTitle')} body={t('analytics.emptyBody')} />}
        columns={[
          {
            header: t('analytics.colName'),
            render: (r) => onPick && r.key !== '∅'
              ? <button type="button" className="mk-linkbtn" onClick={() => onPick(r)}>{r.label}</button>
              : r.label,
          },
          ...columns,
        ]}
      />
    </Panel>
  );
}

function SubjectView({ subject, r, narrow }: { subject: Subject; r: Report; narrow: (f: Drill['field']) => (r: Row) => void }) {
  const { t } = useTranslation();
  const n = (v: unknown) => (typeof v === 'number' || typeof v === 'string' ? String(v) : '—');
  const keyed = <T extends { label: string }>(xs: T[], key: (x: T) => string) => xs.map((x) => ({ ...x, key: key(x) })) as unknown as Row[];

  if (subject === 'inventory') {
    const inv = r.inventory as { asOf: string; pieces: number; wholesale: CurrencyAmount[]; rp: CurrencyAmount[]; consolidated: Consolidated; ageing: Array<{ band: string; pieces: number; wholesale: CurrencyAmount[]; rp: CurrencyAmount[] }>; byCategory: Row[]; byRetailer: Row[] };
    const mv = r.movers as { thresholdDays: number; fast: Array<Row & { productName: string }>; slow: Array<Row & { productName: string }> };
    return (
      <>
        <Tiles items={[
          { k: t('analytics.piecesHeld'), v: inv.pieces, s: t('analytics.asOf', { day: inv.asOf }) },
          { k: t('analytics.wholesaleHeld'), v: <Amounts list={inv.wholesale} /> },
          { k: t('analytics.rpHeld'), v: <Amounts list={inv.rp} /> },
        ]} />
        <Combined c={inv.consolidated} />
        <Breakdown title={t('analytics.ageing')} rows={inv.ageing.map((b) => ({ ...b, key: b.band, label: b.band }))}
          columns={[{ header: t('analytics.pieces'), numeric: true, render: (x) => n(x.pieces) },
            { header: t('analytics.wholesale'), numeric: true, render: (x) => <Amounts list={x.wholesale as CurrencyAmount[]} /> }]} />
        <Breakdown title={t('analytics.fast')} rows={keyed(mv.fast, (x) => (x as unknown as { productId: string }).productId).map((x) => ({ ...x, label: String(x.productName) }))}
          columns={[{ header: t('analytics.sold'), numeric: true, render: (x) => n(x.soldInPeriod) },
            { header: t('analytics.avgDays'), numeric: true, render: (x) => n(x.averageDaysToSale) }]} />
        <Breakdown title={t('analytics.slow', { days: mv.thresholdDays })} rows={keyed(mv.slow, (x) => (x as unknown as { productId: string }).productId).map((x) => ({ ...x, label: String(x.productName) }))}
          columns={[{ header: t('analytics.heldOver'), numeric: true, render: (x) => n(x.heldOverThreshold) },
            { header: t('analytics.oldest'), numeric: true, render: (x) => n(x.oldestDaysHeld) }]} />
        <Breakdown title={t('analytics.byCategory')} rows={inv.byCategory} onPick={narrow('categoryId')}
          columns={[{ header: t('analytics.pieces'), numeric: true, render: (x) => n(x.pieces) },
            { header: t('analytics.wholesale'), numeric: true, render: (x) => <Amounts list={x.wholesale as CurrencyAmount[]} /> }]} />
      </>
    );
  }

  if (subject === 'sales') {
    const s = r.sales as { pieces: number; wholesale: CurrencyAmount[]; consolidated: Consolidated; byRetailer: Row[]; byProduct: Row[]; byCategory: Row[]; byPricePoint: Row[] };
    const d = r.daysToSale as { overall: { pieces: number; averageDays: number | null }; by: Record<string, Row[]> };
    const perf = r.performance as Array<Record<string, unknown> & { retailerId: string; retailerName: string }>;
    const amount = { header: t('analytics.wholesale'), numeric: true, render: (x: Row) => <Amounts list={x.wholesale as CurrencyAmount[]} /> };
    const pieces = { header: t('analytics.pieces'), numeric: true, render: (x: Row) => n(x.pieces) };
    const days = { header: t('analytics.avgDays'), numeric: true, render: (x: Row) => n(x.averageDays) };
    return (
      <>
        <Tiles items={[
          { k: t('analytics.piecesSold'), v: s.pieces },
          { k: t('analytics.wholesaleSold'), v: <Amounts list={s.wholesale} /> },
          { k: t('analytics.avgDaysToSale'), v: n(d.overall.averageDays) },
        ]} />
        <Combined c={s.consolidated} />
        <Breakdown title={t('analytics.byRetailer')} rows={s.byRetailer} onPick={narrow('retailerId')} columns={[pieces, amount]} />
        <Breakdown title={t('analytics.byCategory')} rows={s.byCategory} onPick={narrow('categoryId')} columns={[pieces, amount]} />
        <Breakdown title={t('analytics.byProduct')} rows={s.byProduct} columns={[pieces, amount]} />
        <Breakdown title={t('analytics.byPricePoint')} rows={s.byPricePoint} columns={[pieces, amount]} />
        <Breakdown title={t('analytics.daysByGroup')} rows={d.by.group ?? []} onPick={narrow('groupId')} columns={[pieces, days]} />
        <Breakdown title={t('analytics.daysByColour')} rows={d.by.colour ?? []} columns={[pieces, days]} />
        <Breakdown title={t('analytics.performance')} rows={perf.map((p) => ({ ...p, key: p.retailerId, label: p.retailerName }))} onPick={narrow('retailerId')}
          columns={[
            { header: t('analytics.sellThrough'), numeric: true, render: (x) => (x.sellThroughPct === null ? '—' : `${n(x.sellThroughPct)}%`) },
            { header: t('analytics.avgDays'), numeric: true, render: (x) => n(x.averageDaysToSale) },
            { header: t('analytics.onTime'), numeric: true, render: (x) => (x.paidOnTimePct === null ? '—' : `${n(x.paidOnTimePct)}%`) },
            { header: t('analytics.daysLate'), numeric: true, render: (x) => n(x.averageDaysLate) },
            // AN-06 — the mean of AN-11's daily samples in the period; "—" is not measured, not 0%.
            { header: t('analytics.subject.budget'), numeric: true, render: (x) => (x.budgetUtilisationPct === null ? '—' : `${n(x.budgetUtilisationPct)}%`) },
          ]} />
      </>
    );
  }

  if (subject === 'lead-time') {
    const l = r.leadTime as { orders: number; completed: number; average: Record<string, number | null>; distribution: { total: Array<{ band: string; orders: number }> }; byRetailer: Row[]; byGroup: Row[]; byCategory: Row[]; byProduct: Row[]; pastPromised: Array<{ id: string; reference: string; retailerName: string; dispatchBy: string; daysLate: number }> };
    const avg = (k: string) => ({ header: t(`analytics.stage.${k}`), numeric: true, render: (x: Row) => n((x.average as Record<string, number | null>)[k]) });
    const stageCols = [{ header: t('analytics.orders'), numeric: true, render: (x: Row) => n(x.orders) }, avg('toConfirm'), avg('toShip'), avg('toReceive'), avg('total')];
    return (
      <>
        <Tiles items={[
          { k: t('analytics.orders'), v: l.orders, s: t('analytics.completed', { n: l.completed }) },
          { k: t('analytics.stage.toConfirm'), v: n(l.average.toConfirm) },
          { k: t('analytics.stage.toShip'), v: n(l.average.toShip) },
          { k: t('analytics.stage.total'), v: n(l.average.total) },
        ]} />
        <Breakdown title={t('analytics.distribution')} rows={l.distribution.total.map((b) => ({ key: b.band, label: t('analytics.days', { band: b.band }), orders: b.orders }))}
          columns={[{ header: t('analytics.orders'), numeric: true, render: (x) => n(x.orders) }]} />
        <Breakdown title={t('analytics.pastPromised')} rows={l.pastPromised.map((o) => ({ ...o, key: o.id, label: o.reference }))}
          columns={[{ header: t('analytics.retailer'), render: (x) => n(x.retailerName) },
            { header: t('analytics.dispatchBy'), render: (x) => n(x.dispatchBy) },
            { header: t('analytics.daysLate'), numeric: true, render: (x) => n(x.daysLate) }]} />
        <Breakdown title={t('analytics.byRetailer')} rows={l.byRetailer} onPick={narrow('retailerId')} columns={stageCols} />
        <Breakdown title={t('analytics.byGroup')} rows={l.byGroup} onPick={narrow('groupId')} columns={stageCols} />
        <Breakdown title={t('analytics.byCategory')} rows={l.byCategory} onPick={narrow('categoryId')} columns={stageCols} />
        <Breakdown title={t('analytics.byProduct')} rows={l.byProduct} columns={stageCols} />
      </>
    );
  }

  if (subject === 'returns') {
    const x = r.returns as { returned: number; received: number; ratePct: number | null; wholesaleReturned: CurrencyAmount[]; averageDaysHeldUntilReturned: number | null; daysHeldBeforeRequest: Unavailable; by: Record<string, Row[]> };
    const cols = [
      { header: t('analytics.returned'), numeric: true, render: (y: Row) => n(y.returned) },
      { header: t('analytics.received'), numeric: true, render: (y: Row) => n(y.received) },
      { header: t('analytics.rate'), numeric: true, render: (y: Row) => (y.ratePct === null ? '—' : `${n(y.ratePct)}%`) },
      { header: t('analytics.wholesale'), numeric: true, render: (y: Row) => <Amounts list={y.wholesaleReturned as CurrencyAmount[]} /> },
    ];
    return (
      <>
        <Tiles items={[
          { k: t('analytics.returnRate'), v: x.ratePct === null ? '—' : `${x.ratePct}%`, s: t('analytics.returnedOf', { a: x.returned, b: x.received }) },
          { k: t('analytics.wholesaleReturned'), v: <Amounts list={x.wholesaleReturned} /> },
          { k: t('analytics.daysUntilReturned'), v: n(x.averageDaysHeldUntilReturned) },
        ]} />
        <NotYet u={x.daysHeldBeforeRequest} />
        <Breakdown title={t('analytics.byProduct')} rows={x.by.product ?? []} columns={cols} />
        <Breakdown title={t('analytics.byCategory')} rows={x.by.category ?? []} onPick={narrow('categoryId')} columns={cols} />
        <Breakdown title={t('analytics.byCollection')} rows={x.by.collection ?? []} columns={cols} />
        <Breakdown title={t('analytics.byRetailer')} rows={x.by.retailer ?? []} onPick={narrow('retailerId')} columns={cols} />
        <Breakdown title={t('analytics.byGroup')} rows={x.by.group ?? []} onPick={narrow('groupId')} columns={cols} />
      </>
    );
  }

  if (subject === 'budget') {
    /*
     * AN-11 — one row per retailer and currency, read at its latest daily sample, with the run
     * that drives the low-utilisation flag. Every figure is the API's: the sample is what the
     * one resolver said that day, and headroom is the report's, floored at zero (BG-08).
     */
    const trend = r.budget as BudgetTrendRow[];
    const latest = (x: Row) => (x.series as BudgetTrendRow['series']).at(-1);
    const amount = (k: 'assigned' | 'inUse' | 'headroom') => (x: Row) => {
      const s = latest(x);
      return s ? <Money amount={s[k]} currency={x.currency as string} /> : '—';
    };
    return (
      <Breakdown title={t('analytics.subject.budget')} onPick={narrow('retailerId')}
        rows={trend.map((x) => ({ ...x, key: `${x.retailerId}|${x.currency}`, label: x.retailerName }))}
        columns={[
          { header: t('analytics.sampledOn'), render: (x) => latest(x)?.day ?? '—' },
          { header: t('retailerBudget.assigned'), numeric: true, render: amount('assigned') },
          { header: t('retailerBudget.inUse'), numeric: true, render: amount('inUse') },
          { header: t('analytics.headroom'), numeric: true, render: amount('headroom') },
          { header: t('analytics.utilisation'), numeric: true, render: (x) => { const u = latest(x)?.utilisationPct; return u == null ? '—' : `${n(u)}%`; } },
          { header: t('analytics.lowFor'), numeric: true, render: (x) => (x.lowUtilisation ? <b>{n(x.lowForDays)}</b> : n(x.lowForDays)) },
        ]} />
    );
  }

  if (subject === 'payables') {
    const p = r.payables as { asOf: string; outstanding: CurrencyAmount[]; consolidated: Consolidated; invoicedOpen: CurrencyAmount[]; soldNotInvoiced: CurrencyAmount[]; overdue: CurrencyAmount[]; ageing: Array<{ band: string; invoices: number; open: CurrencyAmount[] }>; byRetailer: Array<{ retailerId: string; retailerName: string; outstanding: CurrencyAmount[] }> };
    return (
      <>
        <Tiles items={[
          { k: t('analytics.outstanding'), v: <Amounts list={p.outstanding} />, s: t('analytics.asOf', { day: p.asOf }) },
          { k: t('analytics.overdue'), v: <Amounts list={p.overdue} /> },
          { k: t('analytics.notInvoiced'), v: <Amounts list={p.soldNotInvoiced} /> },
        ]} />
        <Combined c={p.consolidated} />
        <Breakdown title={t('analytics.ageing')} rows={p.ageing.map((b) => ({ ...b, key: b.band, label: t('analytics.days', { band: b.band }) }))}
          columns={[{ header: t('analytics.invoices'), numeric: true, render: (x) => n(x.invoices) },
            { header: t('analytics.open'), numeric: true, render: (x) => <Amounts list={x.open as CurrencyAmount[]} /> }]} />
        <Breakdown title={t('analytics.byRetailer')} rows={p.byRetailer.map((x) => ({ ...x, key: x.retailerId, label: x.retailerName }))} onPick={narrow('retailerId')}
          columns={[{ header: t('analytics.outstanding'), numeric: true, render: (x) => <Amounts list={x.outstanding as CurrencyAmount[]} /> }]} />
      </>
    );
  }

  if (subject === 'non-moving') return <QuarterlyHoldings r={r.nonMoving as NonMovingReport} />;

  const s = r.subscriptions as { note: string; renewalsDue: number; cameUpForRenewal: number; renewed: number; lapsed: number; cancelled: number; renewalRatePct: number | null; feesRecognised: CurrencyAmount[]; feesConsolidated: Consolidated; lapses: Array<{ retailerId: string; retailerName: string; renewsOn: string }> };
  return (
    <>
      <Alert tone="us">{s.note}</Alert>
      <Tiles items={[
        { k: t('analytics.renewalsDue'), v: s.renewalsDue },
        { k: t('analytics.renewalRate'), v: s.renewalRatePct === null ? '—' : `${s.renewalRatePct}%`, s: t('analytics.renewedOf', { a: s.renewed, b: s.cameUpForRenewal - s.cancelled }) },
        { k: t('analytics.lapsed'), v: s.lapsed },
        { k: t('analytics.feesRecognised'), v: <Amounts list={s.feesRecognised} /> },
      ]} />
      <Combined c={s.feesConsolidated} />
      <Breakdown title={t('analytics.lapses')} rows={s.lapses.map((x) => ({ ...x, key: `${x.retailerId}-${x.renewsOn}`, label: x.retailerName }))}
        columns={[{ header: t('analytics.renewsOn'), render: (x) => n(x.renewsOn) }]} />
    </>
  );
}

/**
 * AN-13 — quarterly holdings, the eighth tab.
 *
 * Two things per retailer, and they answer different questions:
 *
 *   THE COHORT   of the pieces held on the quarter's first day, what sold, came back, was
 *                reported lost, and was still there at its close. A statement of fact, and the
 *                sentence that opens the conversation.
 *   THE RANKING  what they hold TODAY, grouped by product, oldest first, pieces on expand with
 *                their Job Numbers — because a switch-out is executed over Job Numbers (CN-13).
 *
 * NO THRESHOLD, NO BAND, NO FLAG, NO BADGE, AND NO ACTION BUTTON. Days held is shown and nothing
 * is compared against it; there is no Request return, no Recall and no Notify retailer, because
 * AN-13 must not imply a recall and the extra navigation to A-43 is the design rather than
 * friction. Every exclusion is rendered as a plain sentence in a `Note` — never an
 * `Alert tone="stop"`, which would put a warning on a report that decides nothing.
 */
function QuarterlyHoldings({ r }: { r: NonMovingReport }) {
  const { t } = useTranslation();
  const q = r.quarter;
  return (
    <>
      {/* The quarter the SERVER resolved. The screen never computes a label of its own, or there
          are two answers to which quarter this is. */}
      <Tiles items={[
        { k: t('analytics.holdings.quarter'), v: q.label, s: `${q.from} — ${q.to}` },
        { k: t('analytics.holdings.heldToday'), v: r.retailers.reduce((n, x) => n + x.held.pieces, 0), s: t('analytics.asOf', { day: r.asOf }) },
      ]} />
      <Note>{r.note}</Note>
      {/* The one case where the shared filter panel and this tab would otherwise disagree in
          silence: the panel keeps whatever dates the previous tab was given, and the server snaps
          them to a whole quarter (invariant 5). */}
      {q.snappedFrom ? <Note>{t('analytics.holdings.snapped', { quarter: q.label })}</Note> : null}
      {!q.complete ? <Note>{t('analytics.holdings.running', { quarter: q.label, to: q.to })}</Note> : null}
      {r.accountsNotShown.map((x) => (
        <Note key={x.reason}>{t(`analytics.holdings.notShown.${x.reason}`, notShownValues(x))}</Note>
      ))}
      {r.retailers.length === 0
        ? <Panel><Empty title={t('analytics.emptyTitle')} body={t('analytics.emptyBody')} /></Panel>
        : r.retailers.map((x) => <HoldingsRetailer key={x.retailerId} x={x} q={q} asOf={r.asOf} />)}
    </>
  );
}

function HoldingsRetailer(
  { x, q, asOf }: { x: NonMovingRetailer; q: NonMovingReport['quarter']; asOf: string },
) {
  const { t } = useTranslation();
  const n = (v: number | null) => (v === null ? '—' : String(v));
  return (
    <Panel title={x.retailerName} actions={x.groupName ?? undefined}>
      <div className="mk-pbody">
        <p><b>{t('analytics.holdings.cohortHeading', { quarter: q.label })}</b></p>
        <p>{t('analytics.holdings.cohortSentence', cohortValues(x, q))}</p>
        <Rail items={[
          [t('analytics.holdings.stillHeld', { to: q.to }), x.cohort.stillHeld],
          [t('analytics.holdings.heldToday'), x.held.pieces],
          [t('analytics.wholesaleHeld'), <Amounts key="w" list={x.held.wholesale} />],
        ]} />
        <p className="mk-hint">{t('analytics.holdings.firstReceipt', { day: x.firstReceiptOn ?? t('common.notRecorded') })}</p>
        {/* Two numbers, two clocks (invariant 1). Unlabelled, Admin tries to reconcile them. */}
        {needsTwoNumberNote(x) ? <Note>{t('analytics.holdings.twoNumbers', { to: q.to, asOf })}</Note> : null}
        {x.excluded.map((e) => (
          <Note key={e.reason}>{t(`analytics.holdings.excluded.${e.reason}`, excludedValues(e))}</Note>
        ))}
        <p><b>{t('analytics.holdings.rankingHeading')}</b></p>
      </div>
      <Table
        rows={productRows(x)}
        rowKey={(p) => p.key}
        empty={<Empty title={t('analytics.emptyTitle')} body={t('analytics.emptyBody')} />}
        columns={[
          { header: t('analytics.colName'), render: (p) => p.productName },
          { header: t('analytics.byCategory'), render: (p) => p.categoryName ?? t('common.notRecorded') },
          { header: t('analytics.pieces'), numeric: true, render: (p) => p.pieces },
          { header: t('analytics.oldest'), numeric: true, render: (p) => n(p.oldestDaysHeld) },
          { header: t('analytics.wholesale'), numeric: true, render: (p) => <Amounts list={p.wholesale} /> },
          { header: '', render: (p) => <HoldingsPieces p={p} /> },
        ]}
      />
    </Panel>
  );
}

/** The pieces behind a product row, with when each was received — the unit the switch-out is made of. */
function HoldingsPieces({ p }: { p: NonMovingProduct }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="mk-linkbtn" onClick={() => setOpen(!open)}>
        {open ? t('analytics.holdings.hidePieces') : t('analytics.holdings.showPieces')}
      </button>
      {open ? (
        <Table
          rows={pieceRows(p)}
          rowKey={(i) => i.key}
          columns={[
            // Null is SHOWN, never filtered away (invariant 3): a blank received date shortens a
            // report instead of failing it.
            { header: t('analytics.holdings.receivedOn'), render: (i) => i.receivedOn ?? t('common.notRecorded') },
            { header: t('analytics.holdings.daysHeld'), numeric: true, render: (i) => (i.daysHeld === null ? '—' : String(i.daysHeld)) },
            { header: t('analytics.wholesale'), numeric: true, render: (i) => <Money amount={i.wholesaleValue} currency={i.currency} /> },
          ]}
        />
      ) : null}
    </>
  );
}

/** AN-08 — each portal side by side; any combined figure is a total in one currency or a refusal. */
function Compare({ filters }: { filters: Filters }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', 'compare', filters],
    queryFn: () => api.get<{
      portals: Array<{ portal: { id: string; code: string; name: string; currency: string }; piecesHeld: number; wholesaleHeld: CurrencyAmount[]; piecesSold: number; wholesaleSold: CurrencyAmount[]; averageDaysToSale: number | null; averageLeadTimeDays: number | null; returnRatePct: number | null; outstanding: CurrencyAmount[] }>;
      combined: { wholesaleHeld: Consolidated; wholesaleSold: Consolidated; outstanding: Consolidated };
    }>(`/analytics/compare?${query(filters)}`, { noPortal: true }),
  });
  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;
  const n = (v: number | null) => (v === null ? '—' : String(v));
  return (
    <>
      <Panel title={t('analytics.compareTitle')}>
        <Table
          rows={data.portals}
          rowKey={(p) => p.portal.id}
          columns={[
            { header: t('analytics.portal'), render: (p) => `${p.portal.name} (${p.portal.currency})` },
            { header: t('analytics.piecesHeld'), numeric: true, render: (p) => p.piecesHeld },
            { header: t('analytics.wholesaleHeld'), numeric: true, render: (p) => <Amounts list={p.wholesaleHeld} /> },
            { header: t('analytics.piecesSold'), numeric: true, render: (p) => p.piecesSold },
            { header: t('analytics.wholesaleSold'), numeric: true, render: (p) => <Amounts list={p.wholesaleSold} /> },
            { header: t('analytics.avgDaysToSale'), numeric: true, render: (p) => n(p.averageDaysToSale) },
            { header: t('analytics.stage.total'), numeric: true, render: (p) => n(p.averageLeadTimeDays) },
            { header: t('analytics.returnRate'), numeric: true, render: (p) => (p.returnRatePct === null ? '—' : `${p.returnRatePct}%`) },
            { header: t('analytics.outstanding'), numeric: true, render: (p) => <Amounts list={p.outstanding} /> },
          ]}
        />
      </Panel>
      <Panel title={t('analytics.combined')} pad>
        <p><b>{t('analytics.wholesaleHeld')}</b></p><Combined c={data.combined.wholesaleHeld} />
        <p><b>{t('analytics.wholesaleSold')}</b></p><Combined c={data.combined.wholesaleSold} />
        <p><b>{t('analytics.outstanding')}</b></p><Combined c={data.combined.outstanding} />
      </Panel>
    </>
  );
}
